import React, { ReactElement, useMemo } from 'react'
import List, { OrderingHeader, toggleOrder } from 'live/components/ui/List'
import ShowMoreFooter, { ShowMoreFooterProps } from '../ShowMoreFooter/ShowMoreFooter'

import * as S from './styled'

import i18n from 'live/services/i18n'

export interface Column<T = any> {
    key: string | number
    title: string
    dataIndex?: string
    width?: number
    className?: string
    renderColumn?: (column: Column) => React.ReactNode | HTMLElement | string
    renderItem?: (value: any, item: T, index?: number) => React.ReactNode | HTMLElement | string
}

export type Direction = 'asc' | 'desc'

export type OrderState<T = any> = {
    prop: string | keyof T
    dir: Direction
    fn?: Function
}

interface Props<T = any> extends Omit<ShowMoreFooterProps, 'empty'> {
    filterProps?: {
        term: string
        column: keyof T
    }
    columns: Column<T>[]
    data: T[]
    order?: OrderState
    sort: boolean
    onClickRow?: (row: T, rowIndex: number) => void
    renderRow?: (row: T, rowIndex: number, extra?: Object) => void
    maxHeight?: string
    extraRowProps?: Object
}

const defaultDirection: Direction = 'asc'

const getDefaultOrderFunc = (prop: any, dir: 'asc' | 'desc'): Function => {
    return (a: any, b: any) => {
        let valA: string | number = null
        let valB: string | number = null
        if (prop) {
            const dataIndexArr = prop?.split('.')
            for (const idx of dataIndexArr) {
                if (!valA) {
                    valA = a[idx]
                } else {
                    valA = valA[idx]
                }
                if (!valB) {
                    valB = b[idx]
                } else {
                    valB = valB[idx]
                }
            }
        }
        if (typeof valA === 'string' && typeof valB === 'string') {
            valA = `${valA}`.toLowerCase()
            valB = `${valB}`.toLowerCase()
            if (dir === 'asc') {
                return valA.localeCompare(valB)
            }
            return valB.localeCompare(valA)
        } else if (typeof valA === 'number' && typeof valB === 'number') {
            if (dir === 'asc') {
                return valA - valB
            }
            return valB - valA
        }
    }
}

const getDefaultOrder = (columns: Column[]) => {
    const prop = columns.length ? columns[0]['dataIndex'] : ''
    return {
        prop: prop,
        dir: defaultDirection,
        fn: getDefaultOrderFunc(prop, defaultDirection)
    }
}

const GenericList = <T extends any>({
    sort,
    order,
    columns,
    data = [],
    filterProps,
    onClickRow,
    emptyMessage,
    endOfPages,
    endOfPagesMessage,
    fetchNextPage,
    loading,
    renderRow: renderRowProp,
    maxHeight,
    extraRowProps
}: Props<T>): ReactElement<Props<T>> => {
    const [orderState, setOrderState] = React.useState(order || getDefaultOrder(columns))

    const onClickToOrder = React.useCallback(
        (prop: string, defaultOrder: Direction = orderState.dir): void => {
            const newState: OrderState = toggleOrder(
                orderState as { prop: string; dir: Direction },
                prop as string,
                defaultOrder
            )
            newState.fn = getDefaultOrderFunc(newState.prop, newState.dir)
            setOrderState(newState)
        },
        [orderState]
    )
    const renderColumn = ({ key, title, dataIndex, width }: Column<T>) => {
        return (
            <S.ListHeaderItem width={width} key={key}>
                <OrderingHeader
                    key={key}
                    onClick={() => dataIndex && onClickToOrder(dataIndex, defaultDirection)}
                    showArrow={orderState.prop === dataIndex}
                    direction={orderState.dir}
                >
                    {title}
                </OrderingHeader>
            </S.ListHeaderItem>
        )
    }

    const renderRow = (row: T, rowIndex: number) => {
        return (
            <S.ListRow key={rowIndex} onClick={() => onClickRow && onClickRow(row, rowIndex)}>
                {columns?.map(({ dataIndex, renderItem, width, className }, cellIndex: number) => {
                    const key = `${rowIndex}-${cellIndex}`
                    let value = null
                    if (dataIndex) {
                        const dataIndexArr = dataIndex?.split('.')
                        for (const idx of dataIndexArr) {
                            if (!value) {
                                value = row[idx]
                            } else {
                                value = value[idx]
                            }
                        }
                    }

                    return (
                        <S.ListRowItem
                            key={key}
                            className={className}
                            width={width}
                            data-testId={"genericList-listRowItem-column-"+value}
                            title={typeof value === 'string' ? value : undefined}
                        >
                            {renderItem ? renderItem(value, row, rowIndex) : value}
                        </S.ListRowItem>
                    )
                })}
            </S.ListRow>
        )
    }

    const filteredData = useMemo(() => {
        let filteredData = data

        if (filterProps) {
            const { term, column } = filterProps
            if (term && column) {
                filteredData = data.filter(row =>
                    String(row[column])
                        .toUpperCase()
                        .includes(term.toUpperCase())
                )
            }
        }
        return filteredData
    }, [data, filterProps])

    return (
        <S.Container className={'list-container'} maxHeight={maxHeight}>
            <List
                sort={sort}
                order={orderState}
                items={filteredData}
                render={(items: T[]) => {
                    return (
                        <>
                            <S.ListHeader className={'list-header'} key={1}>
                                {columns.map(column => renderColumn(column))}
                            </S.ListHeader>
                            <S.ListBody className={'list-body'} key={2}>
                                {items.map((item, index) =>
                                    renderRowProp ? renderRowProp(item, index, extraRowProps) : renderRow(item, index)
                                )}
                            </S.ListBody>
                        </>
                    )
                }}
            />
            {!filteredData?.length && (
                <S.FooterContainer>
                    <ShowMoreFooter
                        endOfPages={endOfPages}
                        loading={!!loading}
                        fetchNextPage={fetchNextPage}
                        empty={!filteredData?.length}
                        endOfPagesMessage={endOfPagesMessage}
                        emptyMessage={emptyMessage || i18n('No Data')}
                    />
                </S.FooterContainer>
            )}
        </S.Container>
    )
}

export default GenericList
